import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Box, IconButton, Skeleton } from '@mui/material';
import { useTheme } from '@mui/styles';
import { Suspense, useState } from 'react';

import axleLogoSrc from '../../../assets/logo.svg';
import { useCurrentOrganization } from '../../../fetching/api/organization';
import { useBooleanStateAtom } from '../../../utils/booleanState';
import { useTransition } from '../../../utils/cssTransitions';
import { ReturnComponent } from '../../../utils/reactTypes';
import { centerChildren } from '../../utils/centering';
import { distributeHorizontally } from '../../utils/linearLayouts';
import { appBarHeightMobile, isGlobalDrawerOpenAtom } from '../lib/constants';

const GlobalDrawerHeaderLogo = (): ReturnComponent => {
    const organization = useCurrentOrganization();
    const [isOrgLogoLoaded, setIsOrgLogoLoaded] = useState(false);
    const hasOrgLogo = !!organization?.logoUrl;

    // If no org logo, just show Axle logo
    if (!hasOrgLogo) {
        return (
            <img
                src={axleLogoSrc}
                alt='Axle Health logo'
                style={{
                    height: '100%',
                    maxWidth: '100%',
                    objectFit: 'contain',
                }}
            />
        );
    }

    // Otherwise show Axle logo | divider | org logo
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                height: '100%',
                width: '100%',
            }}
        >
            {/* Axle logo on the left */}
            <img
                src={axleLogoSrc}
                alt='Axle Health logo'
                style={{
                    height: '100%',
                    maxWidth: '40%',
                    objectFit: 'contain',
                }}
            />

            {/* Vertical divider */}
            <Box
                sx={{
                    width: '1px',
                    height: '60%',
                    backgroundColor: 'rgba(0, 0, 0, 0.12)',
                }}
            />

            {/* Org logo on the right */}
            <Box
                sx={{
                    flex: 1,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                }}
            >
                {!isOrgLogoLoaded && (
                    <Skeleton
                        variant='rectangular'
                        width='100%'
                        height='70%'
                        sx={{
                            borderRadius: 1,
                            backgroundColor: 'rgba(0, 0, 0, 0.08)',
                        }}
                    />
                )}
                {organization.logoUrl && (
                    <img
                        src={organization.logoUrl}
                        alt='Organization logo'
                        onLoad={() => setIsOrgLogoLoaded(true)}
                        style={{
                            height: '100%',
                            maxWidth: '100%',
                            objectFit: 'contain',
                            opacity: isOrgLogoLoaded ? 1 : 0,
                            position: isOrgLogoLoaded ? 'static' : 'absolute',
                            transition: 'opacity 0.2s ease-in-out',
                        }}
                    />
                )}
            </Box>
        </Box>
    );
};

const LogoSkeleton = (): ReturnComponent => (
    <Skeleton
        variant='rectangular'
        width='100%'
        height='70%'
        sx={{
            borderRadius: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
        }}
    />
);

export const GlobalDrawerHeader = (): ReturnComponent => {
    const theme = useTheme();
    const [isDrawerOpen, openDrawer, closeDrawer] = useBooleanStateAtom(isGlobalDrawerOpenAtom);

    return (
        <Box style={{ transition: useTransition(['opacity']), willChange: 'opacity' }}>
            {/* Chevron right icon in closed mode */}
            <Box {...centerChildren} height={appBarHeightMobile} minHeight={appBarHeightMobile}>
                <IconButton
                    onClick={openDrawer}
                    aria-label='Open navigation drawer'
                    aria-expanded={isDrawerOpen}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: !isDrawerOpen ? 1 : 0,
                        zIndex: !isDrawerOpen ? 1 : 0,
                    }}
                >
                    <ChevronRight />
                </IconButton>
            </Box>
            {/* Wide org/Axle logo in open mode */}
            <Box
                {...distributeHorizontally}
                height={appBarHeightMobile}
                style={{
                    opacity: isDrawerOpen ? 1 : 0,
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    right: 0,
                    padding: theme.spacing(1),
                    paddingLeft: theme.spacing(1), // Match drawer item left spacing
                    willChange: 'opacity',
                }}
            >
                <Box
                    sx={{
                        height: '100%',
                        paddingLeft: theme.spacing(1), // Match drawer item left spacing (8px)
                        position: 'relative',
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <Suspense fallback={<LogoSkeleton />}>
                        <GlobalDrawerHeaderLogo />
                    </Suspense>
                </Box>
                <IconButton
                    onClick={closeDrawer}
                    aria-label='Close navigation drawer'
                    aria-expanded={isDrawerOpen}
                >
                    <ChevronLeft />
                </IconButton>
            </Box>
        </Box>
    );
};
